import api from './api';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { getAppVersion } from '../utils/version';

// Aktuelle App-Version aus zentraler .env Datei (via utils/version.js)
// WICHTIG: Die Versionsnummer wird zentral in der .env Datei im Root-Verzeichnis verwaltet
// Format: "1.0.0" (semantisches Versioning)
// WICHTIG: Zur Laufzeit vom Backend laden, falls verfügbar (verhindert Cache-Probleme)
let CURRENT_VERSION = getAppVersion();

// Versuche Version zur Laufzeit vom Backend zu laden (für Web)
// Dies umgeht Cache-Probleme beim Build
if (typeof window !== 'undefined') {
  // Version wird beim ersten API-Call vom Backend geholt
  // Falls nicht verfügbar, verwende getAppVersion()
  try {
    const versionFromStorage = sessionStorage.getItem('app_version_cache');
    if (versionFromStorage) {
      CURRENT_VERSION = versionFromStorage;
    }
  } catch (error) {
    // Ignoriere Fehler beim Zugriff auf sessionStorage
  }
}

// Storage-Key für gespeicherte Version
const STORAGE_KEY = 'app_version';

/**
 * Prüft, ob eine neue Version verfügbar ist
 */
export const checkForUpdates = async () => {
  try {
    // Server-Version abrufen
    const response = await api.get('/version');
    
    if (response.data.success) {
      const serverVersion = response.data.version;
      
      // WICHTIG: Verwende IMMER die Build-Zeit-Version als Client-Version
      // Die Version wird zur Build-Zeit in den JavaScript-Code eingebaut
      // Wenn das Frontend mit 1.0.0 gebaut wurde, ist CURRENT_VERSION = "1.0.0"
      // Wenn das Frontend mit 1.1.0 gebaut wurde, ist CURRENT_VERSION = "1.1.0"
      const clientVersion = CURRENT_VERSION;
      
      // Lade die gespeicherte Version aus AsyncStorage (nur für Debugging)
      const savedVersion = await AsyncStorage.getItem(STORAGE_KEY);
      
      // Vergleich der Versionen (semantisches Versioning: "1.0.0")
      // WICHTIG: Vergleiche Server-Version mit Build-Zeit Client-Version
      const hasUpdate = compareVersions(serverVersion, clientVersion) > 0;
      
      // Nur wenn eine neue Version verfügbar ist UND der User noch nicht benachrichtigt wurde
      const lastNotifiedVersion = await AsyncStorage.getItem('last_notified_version');
      const alreadyNotified = lastNotifiedVersion === serverVersion;
      
      // Wenn ein Update verfügbar ist UND noch nicht benachrichtigt:
      // - Zeige das Update-Modal → Automatisches Update wird durchgeführt
      // - Bei Web-Apps MUSS das Update durchgeführt werden (keine "Später"-Option)
      
      // Wenn keine gespeicherte Version existiert, speichere die aktuelle Build-Zeit Version
      if (!savedVersion) {
        await AsyncStorage.setItem(STORAGE_KEY, clientVersion);
      }
      
      return {
        hasUpdate: hasUpdate && !alreadyNotified,
        currentVersion: clientVersion,
        serverVersion: serverVersion,
        alreadyNotified: alreadyNotified
      };
    }
    
    return {
      hasUpdate: false,
      currentVersion: CURRENT_VERSION,
      serverVersion: null,
      error: 'Version konnte nicht abgerufen werden'
    };
  } catch (error) {
    // Bei Fehler: keine Update-Benachrichtigung
    console.error('Error checking for updates:', error);
    return {
      hasUpdate: false,
      currentVersion: CURRENT_VERSION,
      serverVersion: null,
      error: error.message
    };
  }
};

/**
 * Speichert, dass der User über eine Version benachrichtigt wurde
 */
export const markVersionAsNotified = async (version) => {
  try {
    await AsyncStorage.setItem('last_notified_version', version);
    // Aktualisiere auch die gespeicherte Version
    await AsyncStorage.setItem(STORAGE_KEY, version);
  } catch (error) {
    console.error('Error marking version as notified:', error);
  }
};

/**
 * Vergleicht zwei Versionsnummern (semantisches Versioning)
 * @returns {number} -1 wenn v1 < v2, 0 wenn v1 === v2, 1 wenn v1 > v2
 */
const compareVersions = (v1, v2) => {
  const parts1 = v1.split('.').map(Number);
  const parts2 = v2.split('.').map(Number);
  
  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const part1 = parts1[i] || 0;
    const part2 = parts2[i] || 0;
    
    if (part1 < part2) return -1;
    if (part1 > part2) return 1;
  }
  
  return 0;
};

/**
 * Gibt die aktuelle App-Version zurück
 */
export const getCurrentVersion = () => {
  return CURRENT_VERSION;
};

